import { Suspense, useEffect } from 'react'
import { Routes, Route, useLocation, Navigate, useParams, useSearchParams } from 'react-router'
import { useAuth } from './contexts/AuthContext'
import { useAuthConfig } from './contexts/AuthConfigContext'
import { useTheme } from './contexts/ThemeContext'
import { useAppMode } from './contexts/AppModeContext'
import { AppLayout } from './components/layout'
import { ErrorBoundary } from './components/ErrorBoundaryWithRouter'
import { GlobalLoader } from './components/ui'
import { TablePageSkeleton } from './components/skeletons'
import { FeatureGuard } from './components/guards/FeatureGuard'
import { PageAccessGuard } from './components/guards/PageAccessGuard'
import { ProjectDetailsAccessGuard } from './components/guards/ProjectDetailsAccessGuard'
import { PlatformAdminGuard } from './components/guards/PlatformAdminGuard'
import { UnauthorizedBanner } from './components/guards/UnauthorizedBanner'
import { BugReportAccessGuard } from './features/bug-report/BugReportAccessGuard'
import { useCanAccess } from './hooks/useCanAccess'
import { useNavVisibility } from './hooks/useNavVisibility'
import { prefetchRoutes } from './hooks/useRoutePrefetch'
import { useToast } from './components/Toast'
// Eagerly imported (tiny): it must render even when chunks can't be fetched during an outage.
import ServiceUnavailablePage from './features/auth/ServiceUnavailablePage'
import { lazyWithChunkRetry } from './utils/lazyLoad'
import './App.css'

/**
 * Code Splitting Implementation
 *
 * All route components are lazy-loaded to improve initial bundle size and load times.
 * Each route is loaded only when needed, reducing the initial JavaScript bundle.
 *
 * Benefits:
 * - Smaller initial bundle size
 * - Faster initial page load
 * - Better performance for users
 * - Routes load on-demand
 */

// Lazy load all route components (lazyWithChunkRetry reloads once on chunk/CSS load failure, e.g. after deploy)
const HomePage = lazyWithChunkRetry(() => import('./features/home/HomePage'))
const PendingApprovalPage = lazyWithChunkRetry(() => import('./features/auth/PendingApprovalPage'))
const ProjectsPage = lazyWithChunkRetry(() => import('./features/projects/ProjectsPage'))
const ProjectDetailsPage = lazyWithChunkRetry(() => import('./features/projects/ProjectDetailsPage'))
const ProjectConfigurePage = lazyWithChunkRetry(() => import('./features/projects/ProjectConfigurePage'))
const ProjectSetupWizard = lazyWithChunkRetry(() => import('./features/projects/components/ProjectSetupWizard'))
const AgentStudioPage = lazyWithChunkRetry(() => import('./features/agent-studio/AgentStudioPage'))
const AgentRegistryPage = lazyWithChunkRetry(() => import('./features/agent-registry/AgentRegistryPage'))
const AgentRegistryChatPage = lazyWithChunkRetry(() => import('./features/agent-registry/AgentRegistryChatPage'))
const AiTransparencyPage = lazyWithChunkRetry(() => import('./features/ai-transparency/AiTransparencyPage'))
const AgentGuardrailsPage = lazyWithChunkRetry(() => import('./features/guardrails/AgentGuardrailsPage'))
const EvalsAndSimulationsPage = lazyWithChunkRetry(() => import('./features/evals-simulations/EvalsAndSimulationsPage'))
const BenchmarksPage = lazyWithChunkRetry(() => import('./features/evals-simulations/BenchmarksPage'))
const HuggingFaceBenchmarksPage = lazyWithChunkRetry(() => import('./features/evals-simulations/HuggingFaceBenchmarksPage'))
const MetricGlossaryPage = lazyWithChunkRetry(() => import('./features/evals-simulations/MetricGlossaryPage'))
const EvalsHelpPage = lazyWithChunkRetry(() => import('./features/evals-simulations/EvalsHelpPage'))
const BenchmarkStudioPage = lazyWithChunkRetry(() => import('./features/benchmark-studio/BenchmarkStudioPage'))
const BenchmarkStudioSessionDetailPage = lazyWithChunkRetry(() => import('./features/benchmark-studio/SessionDetailPage'))
const BenchmarkStudioCommitPage = lazyWithChunkRetry(() => import('./features/benchmark-studio/CommitPage'))
const BenchmarkStudioContributionsPage = lazyWithChunkRetry(() => import('./features/benchmark-studio/ContributionDashboardPage'))
const AnnotatorPortalPage = lazyWithChunkRetry(() => import('./features/benchmark-studio/AnnotatorPortalPage'))
const ModelsPage = lazyWithChunkRetry(() => import('./features/models/ModelsPage'))
const ModelGroupPage = lazyWithChunkRetry(() => import('./features/models/pages/ModelGroupPage'))
const PricingCatalogPage = lazyWithChunkRetry(() => import('./features/models/pages/PricingCatalogPage'))
const PricingHistoryPage = lazyWithChunkRetry(() => import('./features/models/pages/PricingHistoryPage'))
const ToolsPage = lazyWithChunkRetry(() => import('./features/tools/ToolsPage'))
const ToolViewPage = lazyWithChunkRetry(() => import('./features/tools/ToolViewPage'))
const MarketplacePage = lazyWithChunkRetry(() => import('./features/tools/MarketplacePage'))
const PromptsPage = lazyWithChunkRetry(() => import('./features/prompts/PromptsPage'))
const PromptEditPage = lazyWithChunkRetry(() => import('./features/prompts/PromptEditPage'))
const PromptChatPage = lazyWithChunkRetry(() => import('./features/prompts/PromptChatPage'))
const OptimizationRunsPage = lazyWithChunkRetry(() => import('./features/prompts/OptimizationRunsPage'))
const OptimizationRunDetailPage = lazyWithChunkRetry(() => import('./features/prompts/OptimizationRunDetailPage'))
const OptimizationComparisonPage = lazyWithChunkRetry(() => import('./features/prompts/OptimizationComparisonPage'))
const SkillsPage = lazyWithChunkRetry(() => import('./features/skills/SkillsPage'))
const SkillEditPage = lazyWithChunkRetry(() => import('./features/skills/SkillEditPage'))
const DataPage = lazyWithChunkRetry(() => import('./features/data/DataPage'))
const GraphTablePreviewPage = lazyWithChunkRetry(
  () => import('./features/knowledge/pages/GraphTablePreviewPage')
)
const GraphApprovalsPage = lazyWithChunkRetry(
  () => import('./features/knowledge/pages/GraphApprovalsPage')
)
const GraphEvalsPage = lazyWithChunkRetry(
  () => import('./features/knowledge/pages/GraphEvalsPage')
)
const GraphApprovalSessionPage = lazyWithChunkRetry(
  () => import('./features/knowledge/pages/GraphApprovalSessionPage')
)
const ConnectionsPage = lazyWithChunkRetry(() => import('./features/knowledge/ConnectionsPage'))
const TableOntologyPage = lazyWithChunkRetry(() => import('./features/knowledge/pages/TableOntologyPage'))
const ProductDocsEmbedPage = lazyWithChunkRetry(() => import('./features/resources/ProductDocsEmbedPage'))
const AdminPage = lazyWithChunkRetry(() => import('./features/admin/AdminPage'))
const AdminRolesPage = lazyWithChunkRetry(() => import('./features/admin/AdminRolesPage'))
const RolePage = lazyWithChunkRetry(() => import('./features/admin/RolePage'))
const LoginPage = lazyWithChunkRetry(() => import('./features/auth/LoginPage'))
const SettingsPage = lazyWithChunkRetry(() => import('./features/settings/SettingsPage'))
const BugReportsPage = lazyWithChunkRetry(() => import('./features/bug-report/BugReportsPage'))
const AboutPage = lazyWithChunkRetry(() => import('./features/help/AboutPage'))
const UnifiedChatPage = lazyWithChunkRetry(() => import('./features/chat/UnifiedChatPage'))
const SideBySideChatPage = lazyWithChunkRetry(() => import('./features/chat/SideBySideChatPage'))
const AssetAccessPage = lazyWithChunkRetry(() => import('./features/shared/AssetAccessPage'))

// Platform Admin (requires Platform Admin role)
const PlatformAdminPage = lazyWithChunkRetry(() => import('./features/platform-admin/PlatformAdminPage'))
const PlatformTenantsPage = lazyWithChunkRetry(() => import('./features/platform-admin/TenantsPage'))
const PlatformTenantDetailPage = lazyWithChunkRetry(() => import('./features/platform-admin/TenantDetailPage'))
const PlatformFeaturesPage = lazyWithChunkRetry(() => import('./features/platform-admin/FeaturesPage'))
const PlatformDocsRagSettingsPage = lazyWithChunkRetry(() => import('./features/platform-admin/DocsRagSettingsPage'))
const PlatformBrandingSettingsPage = lazyWithChunkRetry(() => import('./features/platform-admin/PlatformBrandingSettingsPage'))
const PlatformGeneralSettingsPage = lazyWithChunkRetry(() => import('./features/platform-admin/PlatformGeneralSettingsPage'))
const PlatformConfigurationsPage = lazyWithChunkRetry(() => import('./features/platform-admin/PlatformConfigurationsPage'))
const PlatformWorkflowsConfigurationPage = lazyWithChunkRetry(() => import('./features/platform-admin/WorkflowsConfigurationPage'))
const PlatformSecurityConfigurationsPage = lazyWithChunkRetry(() => import('./features/platform-admin/PlatformSecurityConfigurationsPage'))
const PlatformDocsChatSessionDetailPage = lazyWithChunkRetry(() => import('./features/platform-admin/DocsChatSessionDetailPage'))
const CrossTenantComparisonPage = lazyWithChunkRetry(() => import('./features/platform-admin/CrossTenantComparisonPage'))
const CustomerAnalyticsPage = lazyWithChunkRetry(() => import('./features/platform-admin/customer-analytics/CustomerAnalyticsPage'))
const PlatformAuditHistoryPage = lazyWithChunkRetry(() => import('./features/platform-admin/AuditHistoryPage'))
const AppLauncherRoute = lazyWithChunkRetry(() => import('./features/app-launcher/AppLauncherRoute'))
const AppSuitesSettingsPage = lazyWithChunkRetry(
  () => import('./features/platform-admin/app-suites/AppSuitesSettingsPage'),
)
// New Feature Pages (Coming Soon)
const AgentStorePage = lazyWithChunkRetry(() => import('./features/agent-store/AgentStorePage'))
const AgentStoreManagePage = lazyWithChunkRetry(() => import('./features/agent-store/AgentStoreManagePage'))
const AgentAboutPage = lazyWithChunkRetry(() => import('./features/agent-store/AgentAboutPage'))
const AgentStoreChatPage = lazyWithChunkRetry(() => import('./features/agent-store/AgentStoreChatPage'))
const WorkflowStorePage = lazyWithChunkRetry(() => import('./features/workflow-store/WorkflowStorePage'))
const WorkflowStoreManagePage = lazyWithChunkRetry(() => import('./features/workflow-store/WorkflowStoreManagePage'))
const WorkflowAboutPage = lazyWithChunkRetry(() => import('./features/workflow-store/WorkflowAboutPage'))
const WorkflowsPage = lazyWithChunkRetry(() => import('./features/workflows/WorkflowsPage'))
const MonitoringPage = lazyWithChunkRetry(() => import('./features/monitoring/MonitoringPage'))
const DeploymentMonitoringPage = lazyWithChunkRetry(() => import('./features/monitoring/DeploymentMonitoringPage'))
const WorkflowDeploymentRunsPage = lazyWithChunkRetry(
  () => import('./features/monitoring/deployment-monitoring/runs/WorkflowDeploymentRunsPage'),
)
const DeploymentRuntimeSettingsPage = lazyWithChunkRetry(() => import('./features/monitoring/DeploymentRuntimeSettingsPage'))
const MonitorPage = lazyWithChunkRetry(() => import('./features/monitoring/deployment-monitoring/MonitorPage'))
const MonitorJobRunsPage = lazyWithChunkRetry(() => import('./features/monitoring/MonitorJobRunsPage'))
const MonitoringTracesPage = lazyWithChunkRetry(() => import('./features/monitoring/MonitoringTracesPage'))

// Agent Auto-Tune Pages
const AgentAutoTuneRunsPage = lazyWithChunkRetry(() => import('./features/agents/pages/AgentAutoTuneRunsPage'))
const AgentAutoTuneMetaPage = lazyWithChunkRetry(() => import('./features/agents/pages/AgentAutoTuneMetaPage'))
const AgentAutoTuneDetailPage = lazyWithChunkRetry(() => import('./features/agents/pages/AgentAutoTuneDetailPage'))
const AgentAutoTuneComparePage = lazyWithChunkRetry(() => import('./features/agents/pages/AgentAutoTuneComparePage'))
const AgentSelfHealPage = lazyWithChunkRetry(() => import('./features/agents/pages/AgentSelfHealPage'))

// Agent Builder Agent (AI Build)
const ABAPage = lazyWithChunkRetry(() => import('./features/agent-studio/aba/ABAPage'))

const IdeatePage = lazyWithChunkRetry(() => import('./features/ideate/IdeatePage'))
const AutopilotBlueprintsPage = lazyWithChunkRetry(() => import('./features/ideate/AutopilotBlueprintsPage'))
const AutopilotBlueprintReadPage = lazyWithChunkRetry(() => import('./features/ideate/AutopilotBlueprintReadPage'))
const BlueprintsListPage = lazyWithChunkRetry(() => import('./features/ideate/blueprints/BlueprintsListPage'))
const BlueprintEditorPage = lazyWithChunkRetry(() => import('./features/ideate/blueprints/BlueprintEditorPage'))
const BlueprintWizardPage = lazyWithChunkRetry(() => import('./features/ideate/blueprints/BlueprintWizardPage'))
const SolutionsPage = lazyWithChunkRetry(() => import('./features/solutions/SolutionsPage'))
const SolutionEditorPage = lazyWithChunkRetry(() => import('./features/solutions/SolutionEditorPage'))
const CWBNextPage = lazyWithChunkRetry(() => import('./features/cwb-next/CWBNextPage'))

// Enterprise Ontology Pages
const OntologyCanvasPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/OntologyCanvasPage'))
const OBAPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/oba/OBAPage'))
const DataModelsPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/DataModelsPage'))
const DataModelDetailPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/DataModelDetailPage'))
const KPILibraryPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/KPILibraryPage'))
const SemanticHubPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/semantic-hub/SemanticHubPage'))
const SemanticModelDetailPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/semantic-hub/pages/SemanticModelDetailPage'))
const OntoEvalPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/OntoEvalPage'))
const OntoEvalSessionDetailPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/OntoEvalSessionDetailPage'))
const DataPipelinesPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/DataPipelinesPage'))
const PipelineDetailPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/PipelineDetailPage'))
const DataObservabilityPage = lazyWithChunkRetry(() => import('./features/enterprise-ontology/pages/DataObservabilityPage'))

// Pipeline Designer Pages
const PipelineDesignerListPage = lazyWithChunkRetry(() => import('./features/data-pipeline-designer/pages/PipelineDesignerListPage').then(m => ({ default: m.PipelineDesignerListPage })))
const PipelineDesignerCanvasPage = lazyWithChunkRetry(() => import('./features/data-pipeline-designer/pages/PipelineDesignerCanvasPage').then(m => ({ default: m.PipelineDesignerCanvasPage })))

// Migrations Studio Pages
const MigrationsStudioPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/MigrationsStudioPage'))
const MigrationWizardPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/MigrationWizardPage'))
const MigrationProjectPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/MigrationProjectPage'))
const ProfilerAgentPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/ProfilerAgentPage'))
const SchemaMapFullscreenPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/SchemaMapFullscreenPage'))
const InterpreterAgentPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/InterpreterAgentPage'))
const TransformerAgentPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/TransformerAgentPage'))
const ValidationAgentPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/ValidationAgentPage'))
const EvaluationAgentPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/EvaluationAgentPage'))
const DEToolsCatalogPage = lazyWithChunkRetry(() => import('./features/migrations-studio/pages/DEToolsCatalogPage'))

/**
 * Loading fallback component for lazy-loaded routes
 *
 * Uses skeleton UI instead of generic loader for better UX.
 * Skeleton mirrors actual page structure for smooth transition.
 *
 * Meta standard: Show structure immediately, load data after.
 */
/** Redirect /autopilot/blueprint/:id → /ideate/blueprint/:id */
function AutopilotBlueprintRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/ideate/blueprint/${id ?? ''}`} replace />
}

/** Redirect legacy /ontology/migrations/:projectId/profiler[/schema-map] → /discovery */
function MigrationProfilerRedirect({ schemaMap = false }: { schemaMap?: boolean }) {
  const { projectId } = useParams<{ projectId: string }>()
  const suffix = schemaMap ? '/schema-map' : ''
  return <Navigate to={`/ontology/migrations/${projectId ?? ''}/discovery${suffix}`} replace />
}

/** Redirect legacy /admin/users → /admin */
function AdminUsersRedirect() {
  const [searchParams] = useSearchParams()
  const tab = searchParams.get('tab')
  const status = searchParams.get('status')
  const newParams = new URLSearchParams(searchParams)
  if (tab === 'registered' || status === 'active') {
    newParams.set('tab', 'registered-users')
  } else if (tab === 'pending' || status === 'pending') {
    newParams.set('tab', 'pending-users')
  }
  return <Navigate to={`/admin?${newParams.toString()}`} replace />
}

const TableRouteLoader = () => <GlobalLoader type="spinner" align="fill" />

/**
 * Smart "/" route: if the user can view home, render HomePage.
 * Otherwise redirect them to the first sidebar item they have access to,
 * so they don't land on a permission-denied screen right after login.
 *
 * Visibility is computed via useNavVisibility — the SAME predicate the sidebar
 * uses — so the landing route is always the first item the sidebar renders.
 * (Previously this re-implemented a looser check that ignored subFeatureId and
 * the docs special-case, so it could redirect to a route not even shown in the
 * sidebar, which looked random across roles.)
 */
function HomeRoute() {
  const { canViewUI, isLoading } = useCanAccess()
  const { firstAccessiblePath } = useNavVisibility()

  // Wait until ALL access checks (auth, tenant, flags) have settled before
  // deciding where to land — otherwise we'd redirect on partially-loaded
  // permissions and pick whichever item happened to resolve first.
  if (isLoading) return null

  if (canViewUI('home')) {
    return (
      <Suspense fallback={<TableRouteLoader />}>
        <ErrorBoundary>
          <HomePage />
        </ErrorBoundary>
      </Suspense>
    )
  }

  const path = firstAccessiblePath()
  if (path) {
    return <Navigate to={path} replace />
  }

  return <UnauthorizedBanner />
}

/** Playbook entry: Evals & Simulations → Benchmarks → studio deep links. */
function RedirectBenchmarkStudioJobFromEvals() {
  const { jobId } = useParams<{ jobId: string }>()
  return <Navigate to={`/benchmark-studio/sessions/${jobId ?? ''}`} replace />
}

function RedirectBenchmarkStudioJobToSession() {
  const { jobId } = useParams<{ jobId: string }>()
  return <Navigate to={`/benchmark-studio/sessions/${jobId ?? ''}`} replace />
}

/** Trace links used to pass `from=/benchmark-studio/:id` (no `/sessions`). */
function RedirectBareBenchmarkStudioId() {
  const { sessionId } = useParams<{ sessionId: string }>()
  return <Navigate to={`/benchmark-studio/sessions/${sessionId ?? ''}`} replace />
}

function RedirectBenchmarkStudioSnapshotToCommit() {
  const { snapshotId } = useParams<{ snapshotId: string }>()
  return <Navigate to={`/benchmark-studio/commits/${snapshotId ?? ''}`} replace />
}

/** Legacy agent-response notification links → studio chat with session selected. */
function RedirectAgentSessionNotification() {
  const { agentId, sessionId } = useParams<{ agentId: string; sessionId: string }>()
  const session = sessionId ? `?session=${encodeURIComponent(sessionId)}` : ''
  return <Navigate to={`/agent-studio/chat/${agentId ?? ''}${session}`} replace />
}

/** Static `<Navigate to="/build">` drops `?projectId=` / `?seedPrompt=`. */
function RedirectToBuildPreserveSearch() {
  const { search, hash } = useLocation()
  return <Navigate to={{ pathname: '/build', search, hash }} replace />
}

function App() {
  const { isAuthenticated, isCheckingAuth, logout, isServiceUnavailable } = useAuth()
  const { isServiceUnavailable: isAuthConfigUnavailable } = useAuthConfig()
  const { loading: themeLoading } = useTheme()
  const { isMilkyway, isDataflow, isApps, experienceResolving } = useAppMode()
  const location = useLocation()
  const toast = useToast()

  // Listen for cancelled tenant errors from API layer
  useEffect(() => {
    const handleTenantCancelled = (event: Event) => {
      const customEvent = event as CustomEvent<{ message: string }>
      const message = customEvent.detail?.message ||
        "Your organization's account is cancelled. Please contact your administrator or support for assistance."
      toast.error(message, 10000)
      // Log user out so they see the login page
      logout()
    }

    window.addEventListener('auth:tenant-cancelled', handleTenantCancelled)
    return () => {
      window.removeEventListener('auth:tenant-cancelled', handleTenantCancelled)
    }
  }, [toast, logout])

  /**
   * Idle-time prefetching for top routes
   *
   * Meta strategy: Prefetch most-visited routes during browser idle time.
   * This ensures they load instantly when user navigates to them.
   *
   * Timing:
   * - Only after user is authenticated
   * - Uses requestIdleCallback (browser idle time)
   * - Staggers requests to avoid network congestion
   * - Fallback to setTimeout for unsupported browsers
   */
  useEffect(() => {
    if (!isAuthenticated) return

    // Define top 3 most-visited routes
    // Based on typical user behavior: Prompts → Projects → Agent Studio
    const topRoutes = ['/prompts', '/projects', '/agent-studio', '/solutions']

    // Prefetch function
    const prefetchTopRoutes = () => {
      // Stagger prefetches: 500ms apart to avoid overwhelming network
      prefetchRoutes(topRoutes, 500)
    }

    // Use requestIdleCallback if available (preferred)
    // Timeout set to 8s so prefetch fires after Lighthouse's measurement window (~3-4s)
    // while still being fast enough to benefit real users before they navigate
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetchTopRoutes, { timeout: 8000 })
    } else {
      // Fallback for Safari and older browsers
      setTimeout(prefetchTopRoutes, 8000)
    }
  }, [isAuthenticated])

  // Show loading state while checking authentication or loading theme
  // Use lighter skeleton for initial load (fewer rows = faster render)
  if (isCheckingAuth || themeLoading) {
    return <TablePageSkeleton rows={5} />
  }

  // Handle pending approval page - shown after OIDC login when user is not yet approved
  // This route must be accessible WITHOUT authentication
  if (location.pathname === '/pending-approval') {
    return (
      <Suspense fallback={<TablePageSkeleton showHeader={false} showPagination={false} rows={1} />}>
        <PendingApprovalPage />
      </Suspense>
    )
  }

  // External annotator portal — tokenized, login-free workbench (no AppLayout)
  if (location.pathname === '/benchmark-studio/portal') {
    return (
      <Suspense fallback={<TablePageSkeleton showHeader={false} showPagination={false} rows={1} />}>
        <AnnotatorPortalPage />
      </Suspense>
    )
  }

  // Backend unreachable (502/503/504 during a deploy): show a blank page with a
  // toast instead of a login form. The login form would be wrong for SSO tenants
  // and unusable for everyone, and the session may still be valid once we're back.
  if (!isAuthenticated && (isServiceUnavailable || isAuthConfigUnavailable)) {
    return <ServiceUnavailablePage />
  }

  // Show login page if not authenticated
  // LoginPage is lazy-loaded but shown immediately, so wrap in Suspense.
  // No onLoginSuccess hook: every login path (password, SSO cookie bootstrap,
  // SSO token callback) updates AuthContext itself, so re-fetching the user here
  // would only add a redundant round trip.
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<TablePageSkeleton showHeader={false} showPagination={false} rows={1} />}>
        <LoginPage />
      </Suspense>
    )
  }

  // Render authenticated app with layout
  // All routes are lazy-loaded and wrapped with ErrorBoundary for better error isolation
  // Suspense provides loading fallback while route components are being loaded

  let rootRouteElement
  if (experienceResolving) {
    rootRouteElement = <TableRouteLoader />
  } else if (isApps) {
    rootRouteElement = <AppLauncherRoute />
  } else if (isMilkyway) {
    rootRouteElement = <Navigate to="/ideate" replace />
  } else if (isDataflow) {
    rootRouteElement = <Navigate to="/ontology/migrations" replace state={{ openFirstGlobe: true }} />
  } else {
    rootRouteElement = <HomeRoute />
  }

  return (
    <AppLayout>
      {/* <Suspense fallback={<TableRouteLoader />}> */}
      <Routes>
        <Route
          path="/"
          element={rootRouteElement}
        />
        <Route
          path="/home"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="home">
                  <HomePage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/projects"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="projects">
                  <ProjectsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/projects/setup-wizard"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="projects" subFeature="setup_wizard">
                  <ProjectSetupWizard />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/projects/:id/configure"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="projects" subFeature="configure">
                  <ProjectConfigurePage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <ProjectDetailsAccessGuard>
                  <ProjectDetailsPage />
                </ProjectDetailsAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/data"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="data">
                  <FeatureGuard feature="data" action="view">
                    <DataPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/data-explorer"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="data">
                  <FeatureGuard feature="data" action="view">
                    <DataPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/data/graph-preview/:tableId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="data">
                  <FeatureGuard feature="data" action="view">
                    <GraphTablePreviewPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/data/graph/:tableId/approvals"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="data">
                  <FeatureGuard feature="data" action="view">
                    <GraphApprovalsPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/data/graph/:tableId/evals"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="data">
                  <FeatureGuard feature="data" action="view">
                    <GraphEvalsPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/data/graph/:tableId/approvals/:runId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="data">
                  <FeatureGuard feature="data" action="view">
                    <GraphApprovalSessionPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        {/* Enterprise Ontology Routes */}
        <Route
          path="/ontology"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="canvas" enforceFlag="ontology_canvas_view">
                  <OntologyCanvasPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/onto-eval"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="onto_eval" enforceFlag="ontology_onto_eval_view">
                  <OntoEvalPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/onto-eval/:sessionId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="onto_eval" enforceFlag="ontology_onto_eval_view">
                  <OntoEvalSessionDetailPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/ai-builder"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="build_with_ai" enforceFlag="ontology_build_with_ai_view">
                  <OBAPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/data-models"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="data_models" enforceFlag="ontology_data_models_view">
                  <DataModelsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/data-models/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="data_models" enforceFlag="ontology_data_models_view">
                  <DataModelDetailPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/semantic-hub/models/:modelId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="semantic_hub" enforceFlag="ontology_semantic_hub_view">
                  <SemanticModelDetailPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/semantic-hub/*"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="semantic_hub" enforceFlag="ontology_semantic_hub_view">
                  <SemanticHubPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/kpi-library/*"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="kpi_library" enforceFlag="ontology_kpi_library_view">
                  <KPILibraryPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/pipelines"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <DataPipelinesPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/pipelines/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PipelineDetailPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/observability"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <DataObservabilityPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        {/* Pipeline Designer Routes */}
        <Route
          path="/pipeline-designer"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PipelineDesignerListPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/pipeline-designer/:designId/canvas"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PipelineDesignerCanvasPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/pipeline-designer/:designId/runs"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PipelineDesignerCanvasPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/pipeline-designer/:designId/runs/:runId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PipelineDesignerCanvasPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <MigrationsStudioPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/new"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <MigrationWizardPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/de-tools"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <DEToolsCatalogPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/:projectId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <MigrationProjectPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/discovery"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <ProfilerAgentPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/discovery/schema-map"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <SchemaMapFullscreenPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/profiler"
          element={
            <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
              <MigrationProfilerRedirect />
            </PageAccessGuard>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/profiler/schema-map"
          element={
            <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
              <MigrationProfilerRedirect schemaMap />
            </PageAccessGuard>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/interpreter"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <InterpreterAgentPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/transformer"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <TransformerAgentPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/validation"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <ValidationAgentPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ontology/migrations/:projectId/evaluation"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ontology" subFeature="migrations" enforceFlag="ontology_migrations_view">
                  <EvaluationAgentPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-studio/ai-builder"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-studio" subFeature="build_with_ai">
                  <ABAPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-studio/auto-tune"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-studio" subFeature="auto_tune">
                  <AgentAutoTuneRunsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-studio/auto-tune/compare"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-studio" subFeature="auto_tune">
                  <AgentAutoTuneComparePage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-studio/auto-tune/meta"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-studio" subFeature="auto_tune">
                  <AgentAutoTuneMetaPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-studio/auto-tune/:sessionId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-studio" subFeature="auto_tune">
                  <AgentAutoTuneDetailPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-studio/self-heal/:agentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-studio">
                  <AgentSelfHealPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-registry/self-heal/:agentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-registry">
                  <AgentSelfHealPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/self-heal/:agentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring">
                  <AgentSelfHealPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-studio/*"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-studio">
                  <AgentStudioPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-registry"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-registry">
                  <AgentRegistryPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-registry/chat/:agentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-registry">
                  <AgentRegistryChatPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ai-transparency"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-registry" enforceFlag="ai_transparency_view">
                  <AiTransparencyPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-registry/guardrails/:agentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-registry" subFeature="agent_guardrails">
                  <AgentGuardrailsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/evals-simulations"
          element={
            // No chunk-load spinner here: the Benchmarks Dataset/Sessions boards
            // show their own circular loader while their card data fetches. A
            // route-level loader too would surface a second spinner in sequence.
            <Suspense fallback={null}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" orSubFeatures={['benchmarks']}>
                  <EvalsAndSimulationsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/evals-simulations/huggingface"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" orSubFeatures={['benchmarks', 'eval_runs']}>
                  <HuggingFaceBenchmarksPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/evals-simulations/help"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations">
                  <EvalsHelpPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/evals-simulations/glossary"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations">
                  <MetricGlossaryPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/simulations"
          element={<Navigate to="/evals-simulations" replace />}
        />
        <Route
          path="/benchmarks"
          element={
            // Dataset/Sessions boards show their own loader (see /evals-simulations).
            <Suspense fallback={null}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" subFeature="benchmarks">
                  <BenchmarksPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/benchmarks/glossary"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" subFeature="benchmarks">
                  <MetricGlossaryPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/benchmarks/huggingface"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" subFeature="benchmarks">
                  <HuggingFaceBenchmarksPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/benchmark-studio"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" subFeature="benchmarks">
                  <BenchmarkStudioPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/benchmark-studio/analytics"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" subFeature="benchmarks">
                  <BenchmarkStudioContributionsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/benchmark-studio/contributions"
          element={<Navigate to="/benchmark-studio/analytics" replace />}
        />
        <Route
          path="/benchmark-studio/sessions/:sessionId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" subFeature="benchmarks">
                  <BenchmarkStudioSessionDetailPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/benchmark-studio/commits/:commitId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="evals-simulations" subFeature="benchmarks">
                  <BenchmarkStudioCommitPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        {/* Legacy redirects: /jobs/:jobId -> /sessions/:sessionId */}
        <Route
          path="/benchmark-studio/jobs/:jobId"
          element={<RedirectBenchmarkStudioJobToSession />}
        />
        {/* Legacy redirects: /snapshots/:snapshotId -> /commits/:commitId */}
        <Route
          path="/benchmark-studio/snapshots/:snapshotId"
          element={<RedirectBenchmarkStudioSnapshotToCommit />}
        />
        <Route
          path="/benchmark-studio/:sessionId"
          element={<RedirectBareBenchmarkStudioId />}
        />
        <Route
          path="/evals-simulations/benchmarks/studio/new"
          element={<Navigate to="/benchmark-studio?new=1" replace />}
        />
        <Route
          path="/evals-simulations/benchmarks/studio/:jobId"
          element={<RedirectBenchmarkStudioJobFromEvals />}
        />
        <Route
          path="/models"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="models">
                  <ModelsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/models/group"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="models">
                  <ModelGroupPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/models/pricing-catalog"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="models">
                  <PricingCatalogPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/models/pricing-catalog/:modelId/history"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="models">
                  <PricingHistoryPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/models/:id/access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="models_manage_access">
                  <AssetAccessPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/tools"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="tools">
                  <ToolsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/tools/marketplace"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="tools">
                  <MarketplacePage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/tools/:id/view"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="tools">
                  <ToolViewPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/tools/:id/access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="tools_manage_access">
                  <AssetAccessPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/prompts"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="prompts">
                  <PromptsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/prompts/optimization"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="prompts" subFeature="prompt_optimization">
                  <OptimizationRunsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/prompts/optimization/compare"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="prompts" subFeature="prompt_optimization">
                  <FeatureGuard feature="prompt_optimization">
                    <OptimizationComparisonPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/prompts/optimization/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="prompts" subFeature="prompt_optimization">
                  <OptimizationRunDetailPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/prompts/:id/edit"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="prompts">
                  <PromptEditPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/prompts/:id/view"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="prompts">
                  <PromptEditPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/prompts/:id/playground"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="prompts">
                  <FeatureGuard flagKey="prompts_playground">
                    <PromptChatPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/skills"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="skills">
                  <SkillsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/skills/new"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="skills">
                  <SkillEditPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/skills/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="skills">
                  <SkillEditPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/skills/:id/edit"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="skills">
                  <SkillEditPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/skills/:id/access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="skills">
                  <FeatureGuard flagKey="skills_manage_access">
                    <AssetAccessPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/connections"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="connections">
                  <ConnectionsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/knowledge/tables/:tableId/ontology"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="knowledge_schema_editor">
                  <TableOntologyPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/knowledge/:id/access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="knowledge_manage_access">
                  <AssetAccessPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/storage/:id/access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="storage_manage_access">
                  <AssetAccessPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/orchestrators/:id/access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="connections_manage_access">
                  <AssetAccessPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/connections/:id/access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="connections_manage_access">
                  <AssetAccessPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/resources/docs"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="docs" enforceFlag="docs_view" bypassRbac>
                  <ProductDocsEmbedPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/admin"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <AdminPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route path="/admin/users" element={<AdminUsersRedirect />} />
        <Route
          path="/admin/roles"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <AdminRolesPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/admin/roles/new"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <RolePage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/admin/roles/:id/view"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <RolePage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/admin/roles/:id/edit"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <RolePage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/settings"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <SettingsPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/help/bug-reports"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <BugReportAccessGuard>
                  <BugReportsPage />
                </BugReportAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/help/about"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <AboutPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/model-chat"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <UnifiedChatPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/model-chat/model-arena"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <FeatureGuard flagKey="chat_model_arena">
                  <SideBySideChatPage />
                </FeatureGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/build"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <CWBNextPage />
              </ErrorBoundary>
            </Suspense>
          }
        />
        {/* Blueprints library — primary routes */}
        <Route
          path="/blueprints"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ai-blueprints">
                  <FeatureGuard feature="ai-blueprints" action="view">
                    <BlueprintsListPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/blueprints/new"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ai-blueprints">
                  <FeatureGuard feature="ai-blueprints" action="view">
                    <BlueprintWizardPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/blueprints/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ai-blueprints">
                  <FeatureGuard feature="ai-blueprints" action="view">
                    <BlueprintEditorPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        {/* Ideate */}
        <Route
          path="/ideate"
          element={
            <ErrorBoundary>
              <PageAccessGuard feature="ideate">
                <Suspense
                  fallback={<GlobalLoader type="spinner" align="fill" />}
                >
                  <IdeatePage />
                </Suspense>
              </PageAccessGuard>
            </ErrorBoundary>
          }
        />
        <Route
          path="/ideate/blueprints"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ideate">
                  <AutopilotBlueprintsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/ideate/blueprint/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="ideate">
                  <AutopilotBlueprintReadPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        {/* Backward-compat redirects from old paths */}
        <Route path="/dip" element={<Navigate to="/ideate" replace />} />
        <Route path="/dip/blueprints" element={<Navigate to="/ideate/blueprints" replace />} />
        <Route
          path="/dip/blueprint/:id"
          element={<Navigate to="/ideate/blueprint/:id" replace />}
        />
        <Route path="/parallelx" element={<Navigate to="/ideate" replace />} />
        <Route
          path="/parallelx/blueprints"
          element={<Navigate to="/ideate/blueprints" replace />}
        />
        <Route
          path="/parallelx/blueprint/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <AutopilotBlueprintRedirect />
            </Suspense>
          }
        />
        <Route path="/autopilot" element={<Navigate to="/ideate" replace />} />
        <Route
          path="/autopilot/blueprints"
          element={<Navigate to="/ideate/blueprints" replace />}
        />
        <Route
          path="/autopilot/blueprint/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <AutopilotBlueprintRedirect />
            </Suspense>
          }
        />
        {/* Backward-compat redirects from old /aqua paths */}
        <Route path="/aqua" element={<Navigate to="/ideate" replace />} />
        <Route path="/aqua/blueprints" element={<Navigate to="/ideate/blueprints" replace />} />
        <Route
          path="/aqua/blueprint/:id"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <AutopilotBlueprintRedirect />
            </Suspense>
          }
        />
        <Route
          path="/solutions"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="solutions">
                  <SolutionsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/solutions/:solutionId/editor"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="solutions">
                  <SolutionEditorPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agents/:agentId/sessions/:sessionId"
          element={<RedirectAgentSessionNotification />}
        />

        {/* New Feature Routes (Coming Soon) */}
        <Route
          path="/agent-store"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-store">
                  <FeatureGuard flagKey="agent_store_view">
                    <AgentStorePage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route path="/agent-store/manage/*" element={<Navigate to="/agent-store" replace />} />
        <Route
          path="/agent-store/:listingId/about"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-store">
                  <FeatureGuard flagKey="agent_store_view">
                    <AgentAboutPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-store/:listingId/chat"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-store">
                  <FeatureGuard flagKey="agent_store_chat">
                    <AgentStoreChatPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/workflow-store"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="workflow-store">
                  <FeatureGuard flagKey="workflow_store_view">
                    <WorkflowStorePage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route path="/workflow-store/manage/*" element={<Navigate to="/workflow-store" replace />} />
        <Route
          path="/workflow-store/:listingId/about"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="workflow-store">
                  <FeatureGuard flagKey="workflow_store_view">
                    <WorkflowAboutPage />
                  </FeatureGuard>
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/agent-workflows/*"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="agent-workflows">
                  <WorkflowsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route path="/agent-workflows/ai-builder" element={<RedirectToBuildPreserveSearch />} />
        <Route
          path="/monitoring/deployments/:deploymentId/manage-access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="deployments">
                  <AgentStoreManagePage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/deployments/:deploymentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="deployments">
                  <DeploymentMonitoringPage type="agent" />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/deployments/:deploymentId/runtime-settings"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="deployments">
                  <DeploymentRuntimeSettingsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/deployments/:deploymentId/monitoring"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="jobs">
                  <MonitorPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/workflow-deployments/:deploymentId/monitoring"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="jobs">
                  <MonitorPage subjectKind="workflow" />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/workflow-deployments/:deploymentId/manage-access"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="deployments">
                  <WorkflowStoreManagePage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/workflow-deployments/:deploymentId/runs"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="deployments">
                  <WorkflowDeploymentRunsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/workflow-deployments/:deploymentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="deployments">
                  <DeploymentMonitoringPage type="workflow" />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/traces/:agentId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring">
                  <MonitoringTracesPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring/jobs/:monitorId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring" subFeature="jobs">
                  <MonitorJobRunsPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/monitoring"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PageAccessGuard feature="monitoring">
                  <MonitoringPage />
                </PageAccessGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/aiservices"
          element={<Navigate to="/evals-simulations?tab=evaluations&subtab=evals" replace />}
        />
        <Route
          path="/aiservices/templates"
          element={<Navigate to="/evals-simulations?tab=evaluations&subtab=templates" replace />}
        />

        <Route path="/cache" element={<Navigate to="/admin?tab=settings&sub=cache" replace />} />

        {/* Platform Admin Routes */}
        <Route
          path="/platform-admin"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformAdminPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/tenants"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformTenantsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/tenants/:tenantId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformTenantDetailPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/features"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformFeaturesPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/documentation"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformDocsRagSettingsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/documentation/sessions/:sessionId"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformDocsChatSessionDetailPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/branding"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformBrandingSettingsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/app-suites"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <AppSuitesSettingsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/general-settings"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformGeneralSettingsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/configurations"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformConfigurationsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/workflows-configuration"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformWorkflowsConfigurationPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/security-configurations"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformSecurityConfigurationsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/comparison"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <CrossTenantComparisonPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/customer-analytics"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <CustomerAnalyticsPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
        <Route
          path="/platform-admin/audit-history"
          element={
            <Suspense fallback={<TableRouteLoader />}>
              <ErrorBoundary>
                <PlatformAdminGuard>
                  <PlatformAuditHistoryPage />
                </PlatformAdminGuard>
              </ErrorBoundary>
            </Suspense>
          }
        />
      </Routes>
      {/* </Suspense> */}
    </AppLayout>
  )
}

export default App
